'use client';

import { usePlausible } from 'next-plausible';
import type { ComponentProps, JSXElementConstructor, MouseEvent } from 'react';

import { Link, type Props as LinkProps } from '@/components/Link/Link';
import { segment } from '@/lib/tracking/segmentAnalytics';

interface DefaultProps extends Omit<LinkProps, 'onClick' | 'as'> {
    eventName: string;
    eventProperties?: Record<string, unknown>;
    plausibleEventName?: string;
}

type Props<T extends JSXElementConstructor<any> | keyof JSX.IntrinsicElements> = T extends never
    ? DefaultProps & { as?: never }
    : DefaultProps & { as: T } & ComponentProps<T>;

export function TrackedLink<T extends JSXElementConstructor<any> | keyof JSX.IntrinsicElements>({
    as: LinkComponent = Link,
    eventName,
    eventProperties,
    plausibleEventName,
    ...props
}: Props<T>) {
    const plausible = usePlausible();

    function handleClick(event: MouseEvent) {
        segment.track(eventName, eventProperties);
        if (plausibleEventName) {
            plausible(plausibleEventName, {
                props: {
                    href: event.currentTarget.getAttribute('href'),
                    label: event.currentTarget.textContent,
                    ...eventProperties,
                },
            });
        }
    }

    return <LinkComponent {...props} onClick={handleClick} />;
}
